import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { toast } from 'sonner';
import { MapPin, Navigation, AlertTriangle, ShieldAlert } from 'lucide-react';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
}

const SetLocation = () => {
  const { user, setUser } = React.useContext(AuthContext);
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const navigate = useNavigate();

  const fetchAddress = useCallback(async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      setAddress(response.data.display_name);
    } catch (error) {
      setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
  }, []);

  const handleMapClick = useCallback((pos) => {
    setPosition(pos);
    fetchAddress(pos[0], pos[1]);
  }, [fetchAddress]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPosition([lat, lng]);
        fetchAddress(lat, lng);
        setLocationLoading(false);
        toast.success('Location detected!');
      },
      () => {
        toast.error('Could not get your location. Please click on the map instead.');
        setLocationLoading(false);
      }
    );
  };

  const handleSaveLocation = async () => {
    if (!position) {
      toast.error('Please set your location first');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const locationData = {
        lat: position[0],
        lng: position[1],
        address: address || `${position[0].toFixed(4)}, ${position[1].toFixed(4)}`
      };

      await axios.put(`${API}/users/profile`, { location: locationData }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUser({ ...user, location: locationData });
      toast.success('Location saved! Welcome to Stray Spotter!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Warning banner */}
        <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl flex items-start gap-3" data-testid="location-required-banner">
          <ShieldAlert className="w-7 h-7 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-amber-800 text-lg">Location Required</h3>
            <p className="text-amber-700 text-sm mt-1">
              Stray Spotter requires your location to notify you about nearby animals in need of rescue. You cannot access the app until your location is set.
            </p>
          </div>
        </div>

        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgb(26, 160, 157)' }}>
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold">Set Your Location</CardTitle>
            <CardDescription className="text-base">
              Hi {user?.name}, please set your location to continue using Stray Spotter
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleUseCurrentLocation}
                data-testid="detect-location-btn"
                variant="outline"
                size="sm"
                disabled={locationLoading}
                style={{ color: 'rgb(26, 160, 157)', borderColor: 'rgb(26, 160, 157)' }}
                className="hover:bg-teal-50"
              >
                {locationLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    Detecting...
                  </div>
                ) : (
                  <>
                    <Navigation className="w-4 h-4 mr-2" />
                    Detect My Location
                  </>
                )}
              </Button>
            </div>

            <div className="h-72 rounded-lg overflow-hidden border-2 border-gray-200" data-testid="set-location-map">
              <MapContainer
                center={[25.2048, 55.2708]}
                zoom={2}
                className="h-full w-full"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <LocationMarker position={position} setPosition={handleMapClick} />
              </MapContainer>
            </div>

            {position && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(26, 160, 157, 0.1)' }}>
                <p className="text-sm text-gray-700 font-medium flex items-center gap-1">
                  <MapPin className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                  {address || 'Fetching address...'}
                </p>
              </div>
            )}

            {!position && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-500">
                  Click "Detect My Location" or tap on the map to set your position.
                </p>
              </div>
            )}

            <Button
              onClick={handleSaveLocation}
              data-testid="save-location-btn"
              className="w-full py-6 text-lg text-white"
              style={{ backgroundColor: 'rgb(26, 160, 157)' }}
              disabled={loading || !position}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </div>
              ) : (
                <>
                  <MapPin className="w-5 h-5 mr-2" />
                  Save Location & Continue
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetLocation;
